import { useState } from 'react';
import { Button, Dialog, Field, Icon, Select, useErrorDialog, useToast } from '@dayon/design-system';
import { api, ApiRequestError } from '../../../lib/api.ts';
import type { SignInStanding } from '../tabs/SecurityTab.tsx';

/**
 * The three credential actions, behind one dialog.
 *
 * ── WHY THESE EXIST AS A SCREEN AT ALL ───────────────────────────────────
 *
 * `services/api/src/modules/player-admin/routes.ts` has been in the codebase
 * since before this dialog: 984 lines, four routes, audit rows, reason
 * vocabularies, superseded-link counts. Nothing in any front end called any of
 * it. The module's own header states the hole it was written to close —
 *
 *   "the operator could stop the OWNER from signing back in, and the
 *    attacker's live session kept working until its own expiry. The one person
 *    guaranteed to be locked out by the incident response was the victim."
 *
 * — and that remained exactly as true after the module landed, because there
 * was no door. A capability with no control is not a capability.
 *
 * ── ONE DIALOG, THREE ACTIONS, AND WHY THAT IS NOT OVER-SHARING ──────────
 *
 * All three ask the same two questions — which reason, and what happened — and
 * differ only in their vocabulary, their consequence paragraph and whether the
 * note is required. Three near-identical files would be three places for the
 * refusal copy to drift, which is how one of them ends up describing a
 * consequence that is no longer true.
 *
 * What is NOT shared is the consequence text: each action states its own, in
 * full, before the press. They are genuinely different acts — one ends
 * sessions and leaves the password working, one takes the password too, one
 * only re-sends an email — and a player phones about all three.
 */

export type CredentialAction = 'SIGN_OUT' | 'FORCE_RESET' | 'RESEND_VERIFICATION';

/** Operator words for each reason code. No screen prints a vocabulary value. */
const REASONS: Record<CredentialAction, readonly { value: string; label: string }[]> = {
  SIGN_OUT: [
    { value: 'SUSPECTED_TAKEOVER', label: 'Somebody else may be in the account' },
    { value: 'DEVICE_LOST', label: 'The player has lost a device' },
    { value: 'SHARED_DEVICE', label: 'Signed in on a shared or public device' },
    { value: 'CREDENTIAL_SHARED', label: 'The password has been shared' },
    { value: 'AT_PLAYER_REQUEST', label: 'The player asked for it' },
    { value: 'INVESTIGATION', label: 'Part of an investigation' },
  ],
  FORCE_RESET: [
    { value: 'SUSPECTED_TAKEOVER', label: 'Somebody else may be in the account' },
    { value: 'CREDENTIAL_IN_BREACH', label: 'The password has appeared in a breach' },
    { value: 'CREDENTIAL_SHARED', label: 'The password has been shared' },
    { value: 'AT_PLAYER_REQUEST', label: 'The player asked for it' },
    { value: 'INVESTIGATION', label: 'Part of an investigation' },
  ],
  RESEND_VERIFICATION: [
    { value: 'NOT_RECEIVED', label: 'They never received the first one' },
    { value: 'LINK_EXPIRED', label: 'The link had already expired' },
    { value: 'FILTERED_AS_SPAM', label: 'It was filtered as spam' },
    { value: 'ADDRESS_CORRECTED', label: 'Their address has been corrected' },
    { value: 'AT_PLAYER_REQUEST', label: 'The player asked for it' },
  ],
};

const SHAPE: Record<
  CredentialAction,
  {
    title: (who: string) => string;
    /*
     * THE WHOLE PATH, built here rather than assembled from a segment.
     *
     * The first version held `path: 'sign-out'` and the caller wrote
     * `/api/players/${id}/${shape.path}`. Nothing was wrong with it except
     * that the route became ungreppable: somebody searching the repo for
     * `force-password-reset` found the API and not its only caller, which is
     * exactly how this module went unreached for weeks in the first place.
     * `credential-routes-have-a-door.test.ts` matches on these.
     */
    url: (id: string) => string;
    confirm: string;
    /** The note's floor. The two credential writes demand one; the resend does not. */
    noteRequired: boolean;
    noteHint: string;
    notePlaceholder: string;
    tone: 'notice-danger' | 'notice-warn' | 'notice notice-quiet';
  }
> = {
  SIGN_OUT: {
    title: (who) => `End every session for ${who}`,
    url: (id) => `/api/players/${id}/sign-out`,
    confirm: 'End every session',
    noteRequired: true,
    noteHint: 'Goes on the audit trail. An investigation will be read back from it.',
    notePlaceholder: 'e.g. player called at 14:10 reporting a login from Cebu they did not make',
    tone: 'notice-danger',
  },
  FORCE_RESET: {
    title: (who) => `Take the password away from ${who}`,
    url: (id) => `/api/players/${id}/force-password-reset`,
    confirm: 'Reset the password',
    noteRequired: true,
    noteHint: 'Goes on the audit trail. An investigation will be read back from it.',
    notePlaceholder: 'e.g. address found in a credential dump shared by the payments provider',
    tone: 'notice-danger',
  },
  RESEND_VERIFICATION: {
    title: (who) => `Send ${who} a new confirmation link`,
    url: (id) => `/api/players/${id}/resend-verification`,
    confirm: 'Send a new link',
    noteRequired: false,
    noteHint: 'Optional. Useful if the reason above does not cover it.',
    notePlaceholder: 'e.g. they read the address back as .con rather than .com',
    tone: 'notice notice-quiet',
  },
};

/** What the server said happened, rendered rather than guessed. */
interface Outcome {
  sessionsEnded?: number;
  supersededResetLinks?: number;
  supersededLinks?: number;
  validForMinutes?: number;
  validForHours?: number;
  /** Null when no message was recorded: the resend answers ALREADY_CONFIRMED with none. */
  notice?: { messageRef: string; status: string; to: string | null } | null;
  /** The resend's own answer: SENT, or ALREADY_CONFIRMED when the address was confirmed first. */
  status?: string;
  /** Ending the sessions leaves the password as it was; these say whether it still works. */
  passwordStillValid?: boolean;
  canSignInAgain?: boolean;
  /** False while the brand is off the floor, which refuses every sign-in whatever the password. */
  brandActive?: boolean;
}

export function CredentialActionDialog({
  action,
  open,
  onClose,
  playerId,
  subject,
  liveSessions,
  standing,
  onDone,
}: {
  action: CredentialAction;
  open: boolean;
  onClose: () => void;
  playerId: string;
  subject: string;
  /** How many sessions are live right now — named in the consequence copy. */
  liveSessions: number;
  /** Whether the player can sign in, as the Security tab read it — named in the consequence copy. */
  standing: SignInStanding | null;
  onDone: () => void;
}) {
  const { showError } = useErrorDialog();
  const { toast } = useToast();
  const shape = SHAPE[action];
  const reasons = REASONS[action];

  const [reasonCode, setReasonCode] = useState<string>('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const trimmed = note.trim();
  const noteError =
    shape.noteRequired && note.length > 0 && trimmed.length < 10
      ? 'At least 10 characters. An investigation is read back from this line.'
      : undefined;
  const ready =
    reasonCode !== '' && (!shape.noteRequired || (trimmed.length >= 10 && trimmed.length <= 500));

  const submit = async () => {
    if (!ready || saving) return;
    setSaving(true);
    try {
      const body = shape.noteRequired
        ? { reasonCode, note: trimmed }
        : trimmed.length > 0
          ? { reasonCode, note: trimmed }
          : { reasonCode };
      const answer = await api.post<Outcome>(shape.url(encodeURIComponent(playerId)), body);

      /*
       * THE SERVER'S OWN COUNTS, never a guess. "Ended 3 sessions" has to be
       * what the UPDATE touched: a dialog that reported the number it saw
       * before the press would claim to have ended a session that expired in
       * between.
       *
       * And the mail line branches on the message's STATUS rather than
       * asserting a send — the status the outbox held when the route recorded
       * it (see `mailWords`), which is the reason the route returns it at all.
       */
      toast(outcomeWords(action, answer));
      setReasonCode('');
      setNote('');
      onClose();
      onDone();
    } catch (error_: unknown) {
      showError({
        title: `Could not ${shape.confirm.toLowerCase()}`,
        message:
          error_ instanceof ApiRequestError
            ? error_.message
            : 'Nothing was changed — the write and its audit row land together or not at all.',
        ...(error_ instanceof ApiRequestError ? { code: error_.code } : {}),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={shape.title(subject)}
      wide
      dismissible={false}
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!ready || saving} onClick={() => void submit()}>
            {saving ? 'Working…' : shape.confirm}
          </Button>
        </>
      }
    >
      <div className="stack">
        <div className={shape.tone === 'notice notice-quiet' ? shape.tone : `notice ${shape.tone}`}>
          <Icon name={shape.tone === 'notice notice-quiet' ? 'info' : 'warning'} />
          <div>{consequence(action, liveSessions, standing)}</div>
        </div>

        <Field label="Why">
          {({ id, describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              value={reasonCode}
              onChange={(e) => setReasonCode(e.target.value)}
            >
              <option value="">Choose a reason…</option>
              {reasons.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field
          label={shape.noteRequired ? 'What happened' : 'What happened (optional)'}
          hint={shape.noteHint}
          {...(noteError === undefined ? {} : { error: noteError })}
        >
          {({ id, describedBy, invalid }) => (
            <textarea
              id={id}
              className="input"
              rows={3}
              value={note}
              maxLength={500}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              onChange={(e) => setNote(e.target.value)}
              placeholder={shape.notePlaceholder}
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
}

/** What each action does, before the press, in full. */
export function consequence(
  action: CredentialAction,
  liveSessions: number,
  standing: SignInStanding | null,
): React.ReactNode {
  const where =
    liveSessions === 0
      ? 'This account has no live session right now'
      : liveSessions === 1
        ? 'This account has one live session'
        : `This account has ${liveSessions} live sessions`;

  if (action === 'SIGN_OUT') {
    return (
      <>
        <p>
          <strong>{where}.</strong> Ending them signs the player out everywhere, on every device,
          at their next tap.
        </p>
        {/* What they can do afterwards is the standing the Security tab read: this press leaves
            the password and the account as they were, and "it still works" is true of neither a
            password a forced reset took nor a suspended account — nor of any account on a brand
            off the floor, whose site refuses every sign-in. The brand's sentence says "while", not
            "until": putting the brand back may not be enough on its own. */}
        {standing === 'BRAND_OFF' ? (
          <p className="text-subtle">
            <strong>The brand this account belongs to is off the floor.</strong> Nobody can sign
            back in while it is: its site refuses every sign-in.
          </p>
        ) : standing === 'SUSPENDED' ? (
          <p className="text-subtle">
            <strong>The account is suspended.</strong> Nobody can sign back in until it is
            reinstated.
          </p>
        ) : standing === 'PASSWORD_TAKEN' ? (
          <p className="text-subtle">
            <strong>Their password does not work.</strong> A forced reset took it away and no new
            one has been set, so nobody can sign back in until the player sets one from a reset
            link.
          </p>
        ) : standing === 'PASSWORD_WORKS' ? (
          <p className="text-subtle">
            <strong>Their password still works.</strong> They can sign straight back in, and so can
            anybody else who has it — if that is the problem, reset the password instead or as well.
          </p>
        ) : (
          <p className="text-subtle">It leaves the password as it was.</p>
        )}
        <p className="text-subtle">
          This changes nothing else on the account: it suspends nothing, restricts nothing and bars
          nothing. It ends sessions and nothing else.
        </p>
      </>
    );
  }

  if (action === 'FORCE_RESET') {
    return (
      <>
        <p>
          <strong>The player&apos;s password stops working immediately</strong>, and{' '}
          {liveSessions === 0 ? 'any session they open' : 'every live session'} ends with it.
          Nobody can sign in to this account until the owner sets a new one.
        </p>
        <p className="text-subtle">
          They are emailed a link to do that. Any reset link already outstanding is cancelled, so
          a link an attacker requested cannot be the one that gets used.
        </p>
        <p className="text-subtle">
          <strong>If they cannot read that email, they cannot get back in.</strong> Check the
          address is theirs before pressing this — on a suspected takeover the address is the
          first thing an attacker changes.
        </p>
      </>
    );
  }

  return (
    <>
      <p>
        A fresh confirmation link is emailed to the address on the account, and any link already
        outstanding stops working.
      </p>
      <p className="text-subtle">
        Nothing else changes: no session ends, the password is untouched, and the account stays
        exactly as it is until the player opens the link.
      </p>
    </>
  );
}

/**
 * WHAT BECAME OF THE EMAIL, for the status the outbox held when the route recorded it.
 *
 * It used to say "RECORDED but not sent — this environment is not delivering mail" for every
 * status but SENT. The route answers at the moment of recording, and wherever mail is live that is
 * PENDING — the worker sends it on its next run, which is every minute — so the dialog claimed on
 * every press that mail was off (staging: MSG-1015 went out fourteen seconds after it said so). An
 * operator told that presses again, which cancels the link the player is about to receive.
 *
 * SUPPRESSED is stamped for three causes, and its sentence holds for each: MAIL_OUTBOUND_MODE=SUPPRESS
 * sends to nobody, ALLOWLIST sends only to the addresses on MAIL_ALLOWLIST, and an address on the
 * do-not-mail list receives nothing in any mode (packages/mailer/src/outbox/suppression.ts). It said
 * "mail is switched off in this environment", which is false under ALLOWLIST: mail is on there, and
 * this address is simply not one the environment sends to. FAILED and DEAD are reachable only when
 * the message was already recorded, and SENDING while the worker holds it.
 */
export function mailWords(mail: Outcome['notice']): string {
  if (mail === undefined || mail === null) return '';
  switch (mail.status) {
    case 'PENDING':
      return ` The email is queued as ${mail.messageRef} and goes out within about a minute.`;
    case 'SENDING':
      return ` The email (${mail.messageRef}) is being handed to the mail service now.`;
    case 'SENT':
      return ` The link was handed to the mail service (${mail.messageRef}).`;
    case 'SUPPRESSED':
      return (
        ` The email (${mail.messageRef}) was not sent: this environment does not send mail to this ` +
        'address, or the address is on the do-not-mail list, so nothing will arrive.'
      );
    case 'FAILED':
    case 'DEAD':
      return ` Sending the email (${mail.messageRef}) failed. Try again, or contact support if it fails again.`;
    default:
      return ` The email was recorded as ${mail.messageRef}.`;
  }
}

/** The success line, built from what the server reported. */
export function outcomeWords(action: CredentialAction, answer: Outcome): string {
  const delivery = mailWords(answer.notice);

  if (action === 'SIGN_OUT') {
    const n = answer.sessionsEnded ?? 0;
    const ended =
      n === 0 ? 'There was no live session to end.' : `Ended ${n} session${n === 1 ? '' : 's'}.`;
    /* Whether the password works is the route's answer, never assumed: a forced reset may have
       taken it, and a suspended account lets nobody back in whatever it is. A brand off the floor
       refuses everybody first, and is named as the brand, never as a suspended account. */
    return (
      ended +
      (answer.brandActive === false
        ? ' Nobody can sign back in while the brand this account belongs to is off the floor.'
        : answer.passwordStillValid === false
          ? ' The password does not work either: a forced reset took it away and no new one has been set.'
          : answer.canSignInAgain === false
            ? ' The account is suspended, so nobody can sign back in.'
            : answer.passwordStillValid === true
              ? ' The password still works.'
              : '')
    );
  }

  if (action === 'FORCE_RESET') {
    const n = answer.sessionsEnded ?? 0;
    const superseded = answer.supersededResetLinks ?? 0;
    const mins = answer.validForMinutes;
    return (
      `The password no longer works and ${n} session${n === 1 ? '' : 's'} ended.` +
      (superseded > 0 ? ` ${superseded} outstanding reset link${superseded === 1 ? '' : 's'} cancelled.` : '') +
      (mins === undefined ? '' : ` The new link is good for ${mins} minutes.`) +
      delivery
    );
  }

  /* Confirmed between the read and the press: the route records nothing and says so. */
  if (answer.status === 'ALREADY_CONFIRMED') {
    return 'This address was already confirmed, so no link was sent.';
  }
  const superseded = answer.supersededLinks ?? 0;
  const hours = answer.validForHours;
  return (
    'A new confirmation link was issued.' +
    (superseded > 0 ? ` ${superseded} earlier link${superseded === 1 ? '' : 's'} cancelled.` : '') +
    (hours === undefined ? '' : ` It is good for ${hours} hours.`) +
    delivery
  );
}
